import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Banner from "@/components/ui/banner";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import {
  useDismissLeadDealSuggestionMutation,
  useLeadDealSuggestion
} from "../../hooks";
import { LeadDealSuggestionStatuses, LeadStatus } from "../../types";

const TRANSLATION_KEY = "leads.leadView.dealSuggestionBanner";

interface Props {
  leadId: string;
  leadStatus: LeadStatus | undefined;
  onConvert: () => void;
}

const LeadDealSuggestionBanner: React.FC<Props> = ({
  leadId,
  leadStatus,
  onConvert
}) => {
  const { t } = useTranslation();
  const { enabled: isFlagEnabled } = useFeatureFlag(
    FeatureFlags.CRM_10630_LEAD_TO_DEAL_SUGGESTION
  );
  // The cached suggestion outlives a status change made on this page, so the
  // live lead status gates it too.
  const isEligible =
    isFlagEnabled &&
    leadStatus !== undefined &&
    LeadDealSuggestionStatuses.includes(leadStatus);
  const { data: suggestion } = useLeadDealSuggestion(leadId, isEligible);
  const { mutate: dismiss } = useDismissLeadDealSuggestionMutation();
  // Banner hides itself on close; remount it after a failed dismiss so the
  // user can retry.
  const [failedDismissals, setFailedDismissals] = useState(0);

  if (!isEligible || !suggestion?.isActionable) {
    return null;
  }

  const title = suggestion.personName
    ? t(`${TRANSLATION_KEY}.titleWithPerson`, {
        person: suggestion.personName
      })
    : t(`${TRANSLATION_KEY}.title`);

  return (
    <Banner
      key={failedDismissals}
      variant="general"
      title={title}
      buttonText={t(`${TRANSLATION_KEY}.convert`)}
      onButtonClick={onConvert}
      showCloseButton={true}
      onClose={() =>
        dismiss(
          { pathParams: { leadId }, data: {} },
          { onError: () => setFailedDismissals((count) => count + 1) }
        )
      }
    />
  );
};

export default LeadDealSuggestionBanner;
